import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { toast } from "sonner";
import { AuthLayout } from "@/components/auth/AuthLayout";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { supabase } from "@/integrations/supabase/client";
import { normalizeAuthEmail } from "@/lib/authEmail";

export const Route = createFileRoute("/forgot-password")({
  head: () => ({
    meta: [{ title: "Reset Password — Currency Thoughts" }, { name: "robots", content: "noindex" }],
    links: [{ rel: "canonical", href: "/forgot-password" }],
  }),
  validateSearch: (search: Record<string, unknown>): { email?: string } => ({
    email: typeof search.email === "string" ? search.email : "",
  }),
  component: ForgotPasswordPage,
});

function ForgotPasswordPage() {
  const search = Route.useSearch();
  const [email, setEmail] = useState(search.email ?? "");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const normalizedEmail = normalizeAuthEmail(email);
    const { error } = await supabase.auth.resetPasswordForEmail(normalizedEmail, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setLoading(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    await supabase.from("activity_logs").insert({
      action: "password_reset",
      target_type: "auth",
      metadata: { email: normalizedEmail },
    });
    toast.success("Password reset email sent.");
  };

  return (
    <AuthLayout eyebrow="Account Recovery" heading="Reset your password">
      <form onSubmit={onSubmit} className="space-y-5">
        <p className="font-sans text-[14px] text-charcoal-500 leading-relaxed">
          Enter the email on your account and we'll send a secure link to create or reset your password.
        </p>
        <div>
          <label
            htmlFor="email"
            className="block font-sans text-[11px] font-semibold uppercase tracking-[0.18em] text-charcoal-500"
          >
            Email
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            inputMode="email"
            className="mt-2 w-full border border-bronze-600/40 bg-cream-50 px-4 py-3 font-sans text-[14px] text-charcoal-900 focus:border-bronze-600 focus:outline-none"
            style={{ borderRadius: "var(--radius-button)" }}
          />
        </div>
        <PrimaryButton type="submit" size="lg" disabled={loading} className="w-full">
          {loading ? "Sending…" : "Send Reset Link"}
        </PrimaryButton>
        <p className="text-center font-sans text-[13px] text-charcoal-500">
          <Link to="/login" className="text-bronze-600 hover:text-bronze-400 font-semibold">
            Back to sign in
          </Link>
        </p>
      </form>
    </AuthLayout>
  );
}
